import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
  Textarea,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { SuccessIcon, WarningMinorIcon } from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GlobalApi, ProvisionApi } from '@Apis';
import CSpinner from 'src/components/CSpinner';
import { useOrgStore } from 'src/models/useOrgModel';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { useSiderWidthStore } from 'src/models/useSiderWidthModel';
import { PolicyType } from '../../../types/app';
import { GrantedAccess } from '../Common/GrantedAccess';

const DISCLAIMER_MAX_LENGTH = 2048;
const HTML_TAG_REGEX = /<[a-z][\s\S]*>/i;

type CalendarAction = 'disclaimer' | 'delete';

interface CalendarEventSettings {
  enabled: boolean;
  action: CalendarAction;
  disclaimerContent: string;
}

const DEFAULT_SETTINGS: CalendarEventSettings = {
  enabled: false,
  action: 'disclaimer',
  disclaimerContent: '',
};

const ExchangeOnlineApiProtectionSettings = React.memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { orgId } = useOrgStore();
  const { siderWidth } = useSiderWidthStore();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isProvisioned, setIsProvisioned] = useState(false);
  const [selectedPolicyType, setSelectedPolicyType] = useState('');
  const [hasPermission, setHasPermission] = useState(false);

  const [savedSettings, setSavedSettings] =
    useState<CalendarEventSettings>(DEFAULT_SETTINGS);
  const [settings, setSettings] =
    useState<CalendarEventSettings>(DEFAULT_SETTINGS);

  const isDirty =
    settings.enabled !== savedSettings.enabled ||
    settings.action !== savedSettings.action ||
    settings.disclaimerContent !== savedSettings.disclaimerContent;

  useEffect(() => {
    loadData();
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-check permission when returning from service-account page (reauth flow)
  const isCheckingPermission = useRef(false);
  useEffect(() => {
    const recheckPermission = async () => {
      if (!isProvisioned || isCheckingPermission.current) return;
      if (document.visibilityState !== 'visible') return;
      isCheckingPermission.current = true;
      try {
        const permissionRes = await GlobalApi.getCalendarEventPermission();
        setHasPermission(permissionRes.data.hasPermission);
      } catch {
        // silently ignore - permission check is non-critical
      } finally {
        isCheckingPermission.current = false;
      }
    };

    document.addEventListener('visibilitychange', recheckPermission);
    window.addEventListener('focus', recheckPermission);

    return () => {
      document.removeEventListener('visibilitychange', recheckPermission);
      window.removeEventListener('focus', recheckPermission);
    };
  }, [isProvisioned]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      // Check provisioning
      const policyTypes = (
        await ProvisionApi.getProvisionedServicesByOrgId(orgId)
      ).data;
      if (!policyTypes.includes(PolicyType.Exchange)) {
        setIsProvisioned(false);
        setSelectedPolicyType(PolicyType.Exchange.toString());
        return;
      }

      setIsProvisioned(true);

      // Load permission status and settings in parallel
      const [permissionRes, settingsRes] = await Promise.all([
        GlobalApi.getCalendarEventPermission(),
        GlobalApi.getCalendarEventSettings(),
      ]);

      setHasPermission(permissionRes.data.hasPermission);
      const data = settingsRes.data;
      const loaded: CalendarEventSettings = {
        ...data,
        disclaimerContent:
          data.disclaimerContent ||
          t('globalSetting.exchangeOnlineApi.disclaimerContent.defaultValue'),
      };
      setSavedSettings(loaded);
      setSettings(loaded);
    } catch (error) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isDirty) return;

    // Validate disclaimer content when action is disclaimer
    if (disclaimerError) {
      window.showNotification({
        titleMsg: disclaimerError,
        severity: 'error',
      });
      return;
    }

    try {
      setSaving(true);
      await GlobalApi.updateCalendarEventSettings({
        enabled: settings.enabled,
        action: settings.action,
        disclaimerContent: settings.disclaimerContent,
      });
      // Update saved baseline on success
      setSavedSettings({ ...settings });
    } catch (error) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSettings({ ...savedSettings });
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSettings((prev) => ({ ...prev, enabled: e.target.checked }));
  };

  const handleActionChange = (value: string) => {
    const action = value as CalendarAction;
    setSettings((prev) => {
      const disclaimerContent =
        action === 'disclaimer' && !prev.disclaimerContent
          ? t('globalSetting.exchangeOnlineApi.disclaimerContent.defaultValue')
          : prev.disclaimerContent;
      return { ...prev, action, disclaimerContent };
    });
  };

  const handleDisclaimerChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setSettings((prev) => ({ ...prev, disclaimerContent: e.target.value }));
  };

  const disclaimerError = useMemo(() => {
    if (settings.action !== 'disclaimer') return undefined;
    if (!settings.disclaimerContent.trim()) {
      return t('globalSetting.exchangeOnlineApi.disclaimerContent.emptyError');
    }
    if (HTML_TAG_REGEX.test(settings.disclaimerContent)) {
      return t('policy.error.disclaimerNoTagContent');
    }
    return undefined;
  }, [settings.action, settings.disclaimerContent, t]);

  const handleDisclaimerBlur = () => {
    if (disclaimerError) {
      window.showNotification({
        titleMsg: disclaimerError,
        severity: 'error',
      });
    }
  };

  const canEdit = withEditPermission && hasPermission;

  return (
    <Box px="4x">
      {(loading || saving) && <CSpinner />}
      {isProvisioned ? (
        <Box pb="60px">
          {/* Page Title */}
          <Text
            mt="28px"
            fontSize="xl"
            lineHeight="xl"
            color={colorStyle.color.primary}
          >
            {t('globalSetting.exchangeOnlineApi.title')}
          </Text>
          <Text mt="4px" color={colorStyle.color.secondary} lineHeight="lg">
            {t('globalSetting.exchangeOnlineApi.description')}
          </Text>

          <Divider my="6x" />

          {/* Calendar Item Support Section */}
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('globalSetting.exchangeOnlineApi.calendarItemSupport.title')}
          </Text>
          <Text
            mt="3x"
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t(
              'globalSetting.exchangeOnlineApi.calendarItemSupport.description'
            )}
          </Text>

          {/* Permission Status */}
          <Box mt="8x">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.exchangeOnlineApi.permissionStatus.title')}
            </Text>
            <Flex mt="3x" alignItems="center">
              {hasPermission ? (
                <>
                  <Icon as={SuccessIcon} color="#04C45A" size="5x" />
                  <Text ml="2x" color={colorStyle.color.primary}>
                    {t(
                      'globalSetting.exchangeOnlineApi.permissionStatus.granted'
                    )}
                  </Text>
                </>
              ) : (
                <>
                  <Icon as={WarningMinorIcon} color="white:primary" size="5x" />
                  <Text ml="2x" color={colorStyle.color.primary}>
                    {t(
                      'globalSetting.exchangeOnlineApi.permissionStatus.notGranted'
                    )}
                  </Text>
                  <Button
                    variant="primary"
                    ml="4x"
                    onClick={() =>
                      navigate('/administration/service-account', {
                        state: { reauth: 'exchange', orgId },
                      })
                    }
                  >
                    {t(
                      'globalSetting.exchangeOnlineApi.permissionStatus.recreateAccessToken'
                    )}
                  </Button>
                </>
              )}
            </Flex>
            <Text
              mt="2x"
              color={colorStyle.color.tertiary}
              fontSize="sm"
              lineHeight="sm"
            >
              {t(
                'globalSetting.exchangeOnlineApi.permissionStatus.description'
              )}
            </Text>
          </Box>

          {/* Enable Calendar Item Actions */}
          <Box mt="8x">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.exchangeOnlineApi.enableCalendarActions.title')}
            </Text>
            <Flex mt="3x" alignItems="center">
              <Switch
                disabled={!canEdit}
                checked={settings.enabled}
                onChange={handleToggleChange}
              />
              <Text
                ml="2x"
                color={colorStyle.color.secondary}
                lineHeight="lg"
                maxWidth={900}
              >
                {t(
                  'globalSetting.exchangeOnlineApi.enableCalendarActions.description'
                )}
              </Text>
            </Flex>
          </Box>

          {/* Calendar Item Action (shown when enabled) */}
          {settings.enabled && (
            <Box mt="8x">
              <Text
                fontSize="lg"
                lineHeight="lg"
                color={colorStyle.color.primary}
              >
                {t('globalSetting.exchangeOnlineApi.calendarItemAction.title')}
              </Text>
              <Text
                mt="3x"
                color={colorStyle.color.secondary}
                lineHeight="lg"
                maxWidth={1000}
              >
                {t(
                  'globalSetting.exchangeOnlineApi.calendarItemAction.description'
                )}
              </Text>

              <Box mt="4x">
                <RadioGroup
                  value={settings.action}
                  onChange={handleActionChange}
                >
                  <Stack direction="column" spacing="4x">
                    <Radio
                      name="disclaimer"
                      value="disclaimer"
                      disabled={!canEdit}
                    >
                      {t(
                        'globalSetting.exchangeOnlineApi.calendarItemAction.addDisclaimer'
                      )}
                    </Radio>

                    {/* Disclaimer Content (shown when "Add disclaimer" is selected) */}
                    {settings.action === 'disclaimer' && (
                      <Box ml="6x">
                        <Text
                          color={colorStyle.color.secondary}
                          lineHeight="lg"
                          maxWidth={900}
                        >
                          {t(
                            'globalSetting.exchangeOnlineApi.disclaimerContent.description'
                          )}
                        </Text>
                        <Flex mt="3x" gap="3x" alignItems="flex-start">
                          <Text
                            color={colorStyle.color.primary}
                            fontSize="sm"
                            lineHeight="lg"
                            flexShrink={0}
                          >
                            {t(
                              'globalSetting.exchangeOnlineApi.disclaimerContent.label'
                            )}
                          </Text>
                          <Box maxWidth={700} flex={1}>
                            <Textarea
                              disabled={!canEdit}
                              value={settings.disclaimerContent}
                              onChange={handleDisclaimerChange}
                              onBlur={handleDisclaimerBlur}
                              rows={5}
                              maxLength={DISCLAIMER_MAX_LENGTH}
                              resize="vertical"
                            />
                            <Flex mt="1x" justifyContent="flex-end">
                              <Text
                                color={colorStyle.color.tertiary}
                                fontSize="xs"
                              >
                                {`${settings.disclaimerContent.length}/${DISCLAIMER_MAX_LENGTH}`}
                              </Text>
                            </Flex>
                          </Box>
                        </Flex>
                      </Box>
                    )}

                    <Radio name="delete" value="delete" disabled={!canEdit}>
                      {t(
                        'globalSetting.exchangeOnlineApi.calendarItemAction.deleteCalendarItem'
                      )}
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>
            </Box>
          )}

          {/* Note Section */}
          {settings.enabled && (
            <Flex mt="8x" mb="8x" alignItems="baseline" gap="2x">
              <Text fontWeight="semibold" flexShrink={0} whiteSpace="nowrap">
                {t('globalSetting.exchangeOnlineApi.note.title')}:
              </Text>
              <Box as="ol" pl="5x" color={colorStyle.color.secondary}>
                <Box as="li" mb="1x">
                  <Text lineHeight="lg">
                    {t('globalSetting.exchangeOnlineApi.note.item1')}
                  </Text>
                </Box>
                <Box as="li" mb="1x">
                  <Text lineHeight="lg">
                    {t('globalSetting.exchangeOnlineApi.note.item2')}
                  </Text>
                </Box>
                <Box as="li">
                  <Text lineHeight="lg">
                    {t('globalSetting.exchangeOnlineApi.note.item3')}
                  </Text>
                </Box>
              </Box>
            </Flex>
          )}

          <Flex
            position="fixed"
            bottom={0}
            left={270 + siderWidth}
            right={0}
            height={64}
            alignItems="center"
            pl="5x"
            gap="2x"
            background={colorStyle.background.secondary}
          >
            <Button
              variant="primary"
              disabled={!isDirty || saving}
              onClick={handleSave}
            >
              {t('common.button.save')}
            </Button>
            <Button
              variant="default"
              disabled={!isDirty}
              onClick={handleCancel}
            >
              {t('common.button.cancel')}
            </Button>
          </Flex>
        </Box>
      ) : null}

      {!isProvisioned && !loading ? (
        <GrantedAccess selectedPolicyType={selectedPolicyType} orgId={orgId} />
      ) : null}
    </Box>
  );
});

export default ExchangeOnlineApiProtectionSettings;
